import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Modal, Form, Row, Col, Badge, Card, Button, InputGroup, Spinner } from 'react-bootstrap';
import { getDrivers, createDriver, updateDriver, getContracts, createContract, openDriverSession, getBases, getBanks, getVehicles, closeDriverSession, importHistoricalContracts, getProviders, getSettlementGroups, getDriverDocumentsAdmin, uploadDriverDocumentAdmin, reviewDocument } from '../api';
import MultiSearchSelect from './MultiSearchSelect';
import apiClient from '../../api';
import DriverAvailabilityModal from '../portal/DriverAvailabilityModal';

const REQUIRED_DOC_TYPES = [
    { key: 'licencia_frontal', label: '🪪 Licencia de Conducir (Frente)', required: true, is_critical: true, group: 'Conductor' },
    { key: 'licencia_reverso', label: '🪪 Licencia de Conducir (Reverso / Control)', required: true, is_critical: true, group: 'Conductor' },
    { key: 'rut_frontal', label: '🆔 Cédula de Identidad (Frente)', required: false, is_critical: false, group: 'Conductor' },
    { key: 'rut_reverso', label: '🆔 Cédula de Identidad (Reverso)', required: false, is_critical: false, group: 'Conductor' },
    { key: 'antecedentes', label: '📜 Certificado de Antecedentes', required: false, is_critical: false, group: 'Conductor' },
    { key: 'decreto_mtt', label: '🏛️ Decreto del MTT (Decreto 80/212)', required: true, is_critical: true, group: 'Vehículo' },
    { key: 'revision_tecnica', label: '🔧 Revisión Técnica del Vehículo', required: true, is_critical: true, group: 'Vehículo' },
    { key: 'soap', label: '🛡️ Seguro Obligatorio (SOAP)', required: true, is_critical: true, group: 'Vehículo' },
    { key: 'seguro_asientos', label: '💺 Seguro de Asientos / Pasajeros', required: true, is_critical: true, group: 'Vehículo' },
    { key: 'permiso_circulacion', label: '🚗 Permiso de Circulación', required: true, is_critical: true, group: 'Vehículo' },
    { key: 'padron', label: '📄 Padrón del Vehículo', required: false, is_critical: false, group: 'Vehículo' },
    { key: 'foto_vehiculo', label: '📷 Foto del Vehículo', required: false, is_critical: false, group: 'Vehículo' }
];

const DriversView = () => {
    const [drivers, setDrivers] = useState([]);
    const [owners, setOwners] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [bases, setBases] = useState([]);
    const [banks, setBanks] = useState([]);
    const [vehicles, setVehicles] = useState([]); 
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [settlementGroups, setSettlementGroups] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [mobileSearchTerm, setMobileSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [limit] = useState(50);
    const [hasMore, setHasMore] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const [onlyPendingDocs, setOnlyPendingDocs] = useState(false);
    
    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Schedule & Availability Modal State (Admin)
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedDriverForSchedule, setSelectedDriverForSchedule] = useState(null);

    // Document Modal State
    const [showDocsModal, setShowDocsModal] = useState(false);
    const [selectedDriverForDocs, setSelectedDriverForDocs] = useState(null);
    const [driverDocs, setDriverDocs] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [uploadingDocType, setUploadingDocType] = useState(null);

    // Mini-form review states
    const [showReviewConfirmModal, setShowReviewConfirmModal] = useState(false);
    const [selectedDocForReview, setSelectedDocForReview] = useState(null);
    const [reviewStatus, setReviewStatus] = useState('approved');
    const [rejectionReason, setRejectionReason] = useState('');
    const [docExpirationDate, setDocExpirationDate] = useState('');
    
    // Form State
    const initialFormState = {
        full_name: '', rut: '', phone: '', email: '',
        is_owner: false, is_driver: true, employer_id: '', contract_id: '',
        provider_id: '',
        base_id: '', 
        permitted_base_ids: [], 
        permitted_vehicle_ids: [],
        current_status: 'offline',
        // CAMPOS BANCARIOS
        bank_id: '',
        account_type: '',
        account_number: '',
        account_rut: '',
        email_notification: '',
        show_pricing: true,
        is_approved: false,
        is_active: true,
        settlement_group_id: '',
        // MTT Y LICENCIA
        license_expiry: '',
        license_type: '',
        only_booking: false
    };
    const [formData, setFormData] = useState(initialFormState);

    // Contract Modal
    const [showContractModal, setShowContractModal] = useState(false);
    const [newContract, setNewContract] = useState({ name: '', type: 'FIJO', percentage_1: 0, percentage_2: 0, threshold: 100000 });

    // Historical Contracts Import
    const [importingContracts, setImportingContracts] = useState(false);
    const contractsFileInputRef = useRef(null);

    const handleImportContracts = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!window.confirm(`¿Cargar archivo "${file.name}" para actualizar contratos históricos?`)) {
            if (contractsFileInputRef.current) contractsFileInputRef.current.value = "";
            return;
        }

        setImportingContracts(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await importHistoricalContracts(formData);
            alert(`¡Éxito! Se importaron/actualizaron ${res.count} contratos históricos.`);
            loadData();
        } catch (e) {
            alert("Error al importar contratos históricos. Revisa el formato.");
        } finally {
            setImportingContracts(false);
            if (contractsFileInputRef.current) contractsFileInputRef.current.value = "";
        }
    };

    const getDriverStatus = (driver) => {
        if (!driver.has_active_session) return 'offline';
        if (!driver.last_heartbeat) return 'offline';
        
        const heartbeatTime = new Date(driver.last_heartbeat).getTime();
        const now = new Date().getTime();
        const diffMinutes = (now - heartbeatTime) / 60000;
        
        if (diffMinutes > 5) {
            return 'offline';
        }
        
        return driver.is_online ? 'online' : 'paused';
    };

    const formatLastHeartbeat = (driver) => {
        if (!driver.last_heartbeat) return 'Sin registros';
        const heartbeatTime = new Date(driver.last_heartbeat).getTime();
        const now = new Date().getTime();
        const diffSeconds = Math.floor((now - heartbeatTime) / 1000);
        
        if (diffSeconds < 60) return 'Hace instantes';
        
        const diffMinutes = Math.floor(diffSeconds / 60);
        if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
        
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
        
        return new Date(driver.last_heartbeat).toLocaleDateString('es-CL', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const loadData = useCallback(async () => {
        try {
            const params = {
                skip: page * limit,
                limit: limit,
                q: searchTerm
            };
            if (selectedProvider) {
                params.provider_id = parseInt(selectedProvider);
            }
            if (onlyPendingDocs) {
                params.has_pending_docs = true;
            }
            const [dData, cData, bData, bankData, vData, pData, sgData, oData] = await Promise.all([
                (searchTerm || mobileSearchTerm || showAll || onlyPendingDocs || selectedProvider) ? getDrivers(mobileSearchTerm ? { limit: 1000, ...(selectedProvider ? { provider_id: parseInt(selectedProvider) } : {}) } : params) : Promise.resolve([]), 
                getContracts(),
                getBases(),
                getBanks(),
                getVehicles(),
                getProviders(),
                getSettlementGroups(),
                getDrivers({ limit: 1000, is_owner: true })
            ]);
            
            let finalDrivers = dData;
            if (mobileSearchTerm) {
                finalDrivers = dData.filter(d => 
                    d.assigned_vehicle_code === mobileSearchTerm || 
                    (d.assigned_vehicle_code && d.assigned_vehicle_code.replace('M-', '') === mobileSearchTerm)
                );
            }
            
            setDrivers(finalDrivers);
            setHasMore(dData.length === limit);
            setContracts(cData);
            setBases(bData);
            setBanks(bankData);
            setVehicles(vData);
            setProviders(pData || []);
            setSettlementGroups(sgData || []);
            setOwners(oData || []);
        } catch (error) {
            console.error("Error loading drivers data", error);
        }
    }, [page, limit, searchTerm, mobileSearchTerm, showAll, onlyPendingDocs, selectedProvider]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            loadData();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [loadData]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(0);
    };

    const loadDriverDocs = useCallback(async (driverId) => {
        setLoadingDocs(true);
        try {
            const docs = await getDriverDocumentsAdmin(driverId);
            setDriverDocs(docs);
        } catch (e) {
            console.error("Error cargando documentos del chofer", e);
        } finally {
            setLoadingDocs(false);
        }
    }, []);

    const handleOpenDocsModal = (driver) => {
        setSelectedDriverForDocs(driver);
        setDriverDocs([]);
        setShowDocsModal(true);
        loadDriverDocs(driver.id);
    };

    const handleUploadDoc = async (documentType, file) => {
        if (!file) return;
        setUploadingDocType(documentType);
        try {
            await uploadDriverDocumentAdmin(selectedDriverForDocs.id, documentType, file);
            alert("Documento subido y procesado con éxito.");
            loadDriverDocs(selectedDriverForDocs.id);
        } catch (e) {
            alert("Error al subir documento: " + (e.response?.data?.detail || e.message));
        } finally {
            setUploadingDocType(null);
        }
    };

    const handleReviewClick = (doc) => {
        setSelectedDocForReview(doc);
        setReviewStatus('approved');
        setRejectionReason('');
        setDocExpirationDate(doc.expiration_date || '');
        setShowReviewConfirmModal(true);
    };

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        try {
            await reviewDocument(selectedDocForReview.id, reviewStatus, rejectionReason, docExpirationDate);
            alert("Revisión del documento guardada con éxito.");
            setShowReviewConfirmModal(false);
            loadDriverDocs(selectedDriverForDocs.id);
        } catch (e) {
            alert("Error al guardar revisión: " + (e.response?.data?.detail || e.message));
        }
    };

    const handleOpenCreate = () => {
        setFormData(initialFormState);
        setIsEditing(false);
        setEditingId(null);
        setShowModal(true);
    };

    const handleOpenEdit = (driver) => {
        const permittedIds = driver.permitted_bases ? driver.permitted_bases.map(b => b.id) : [];
        const permittedVehicleIds = driver.permitted_vehicles ? driver.permitted_vehicles.map(v => v.id) : [];
        
        setFormData({
            full_name: driver.full_name,
            rut: driver.rut,
            phone: driver.phone || '',
            email: driver.email || '',
            is_owner: driver.is_owner,
            is_driver: driver.is_driver,
            employer_id: driver.employer_id || '',
            contract_id: driver.contract_id || '',
            provider_id: driver.provider_id || '',
            base_id: driver.base_id || '',
            permitted_base_ids: permittedIds,
            permitted_vehicle_ids: permittedVehicleIds,
            current_status: driver.current_status,
            // CAMPOS BANCARIOS
            bank_id: driver.bank_id || '',
            account_type: driver.account_type || '',
            account_number: driver.account_number || '',
            account_rut: driver.account_rut || '',
            email_notification: driver.email_notification || '',
            show_pricing: driver.show_pricing !== false,
            is_approved: !!driver.is_approved,
            is_active: driver.is_active !== false,
            settlement_group_id: driver.settlement_group_id || '',
            // MTT Y LICENCIA
            license_expiry: driver.license_expiry || '',
            license_type: driver.license_type || '',
            only_booking: !!driver.only_booking
        });
        setIsEditing(true);
        setEditingId(driver.id);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                employer_id: formData.employer_id ? parseInt(formData.employer_id) : null,
                contract_id: formData.contract_id ? parseInt(formData.contract_id) : null,
                provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
                base_id: formData.base_id ? parseInt(formData.base_id) : null,
                bank_id: formData.bank_id ? parseInt(formData.bank_id) : null,
                settlement_group_id: formData.settlement_group_id ? parseInt(formData.settlement_group_id) : null,
                license_expiry: formData.license_expiry || null,
                license_type: formData.license_type || null,
                only_booking: !!formData.only_booking,
                permitted_base_ids: formData.permitted_base_ids.map(Number),
                permitted_vehicle_ids: formData.permitted_vehicle_ids.map(v => typeof v === 'object' ? v.id : Number(v)).filter(n => !isNaN(n) && n > 0)
            };

            if (isEditing) {
                const updated = await updateDriver(editingId, payload);
                setDrivers(prev => prev.map(d => d.id === editingId ? { ...d, ...updated } : d));
            } else {
                await createDriver(payload);
            }
            setShowModal(false);
            setTimeout(loadData, 200);
        } catch (error) {
            alert("Error al guardar conductor. Verifique el RUT.");
        }
    };

    const handleOpenSession = async (driver) => {
        if (!window.confirm(`¿Estás seguro de que deseas iniciar la sesión de ${driver.full_name} remotamente? El conductor podrá recibir servicios programados.`)) {
            return;
        }
        try {
            await openDriverSession(driver.id);
            alert("Sesión iniciada correctamente.");
            loadData();
        } catch (error) {
            console.error("Error opening session", error);
            alert("Error al iniciar la sesión.");
        }
    };

    const handleCloseSession = async (driver) => {
        if (!window.confirm(`¿Estás seguro de que deseas cerrar la sesión de ${driver.full_name} remotamente? El conductor se desconectará de inmediato.`)) {
            return;
        }
        try {
            await closeDriverSession(driver.id);
            alert("Sesión cerrada correctamente.");
            loadData();
        } catch (error) {
            console.error("Error closing session", error);
            alert("Error al cerrar la sesión.");
        }
    };

    const handleContractSubmit = async () => {
        if (!newContract.name) return;
        await createContract(newContract);
        setShowContractModal(false);
        setNewContract({ name: '', type: 'FIJO', percentage_1: 0, percentage_2: 0, threshold: 100000 });
        loadData();
    };

    const getEmployerName = (id) => {
        if (!id) return null;
        const employer = owners.find(o => o.id === id) || drivers.find(d => d.id === id);
        return employer ? employer.full_name : 'ID: ' + id;
    };


    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>👨‍✈️ Gestión de Conductores</h3>
                <div className="d-flex gap-2">
                    <Form.Control 
                        placeholder="🔢 Nº Móvil..." 
                        value={mobileSearchTerm} 
                        onChange={(e) => { setMobileSearchTerm(e.target.value); setPage(0); }} 
                        style={{maxWidth: '120px'}}
                    />
                    <Form.Control 
                        placeholder="🔍 Buscar por Nombre o RUT..." 
                        value={searchTerm} 
                        onChange={handleSearchChange} 
                        style={{maxWidth: '260px'}}
                    />
                    <Form.Select 
                        value={selectedProvider} 
                        onChange={(e) => { setSelectedProvider(e.target.value); setPage(0); }} 
                        style={{maxWidth: '210px'}}
                    >
                        <option value="">🏢 Todos los Proveedores</option>
                        {providers.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                    <Button variant="outline-primary" onClick={() => { setShowAll(true); setOnlyPendingDocs(false); }}>👁️ Ver Todos</Button>
                    <Button 
                        variant={onlyPendingDocs ? "warning" : "outline-warning"} 
                        onClick={() => { setOnlyPendingDocs(!onlyPendingDocs); setShowAll(false); setPage(0); }}
                        className={onlyPendingDocs ? "fw-bold shadow-sm" : ""}
                    >
                        ⚠️ Doc. Pendientes
                    </Button>
                    <Button variant="outline-primary" onClick={() => setShowContractModal(true)}>+ Contrato</Button>
                    <input 
                        type="file" 
                        ref={contractsFileInputRef} 
                        onChange={handleImportContracts} 
                        accept=".xlsx, .xls, .csv" 
                        style={{ display: 'none' }} 
                    />
                    <Button 
                        variant="outline-success" 
                        onClick={() => contractsFileInputRef.current?.click()}
                        disabled={importingContracts}
                    >
                        {importingContracts ? "Importando..." : "📥 Contratos Históricos"}
                    </Button>
                    <Button variant="success" onClick={handleOpenCreate}>+ Nuevo Ingreso</Button>
                </div>
            </div>

            <Card className="shadow-sm border-0">
                <Table hover responsive className="align-middle mb-0">
                    <thead className="bg-light">
                        <tr>
                            <th>Nombre / RUT</th>
                            <th>Móvil Actual</th>
                            <th>Licencia MTT</th>
                            <th>Base (Pago)</th>
                            <th>Rol</th>
                            <th>Contrato</th>
                            <th>Sesión</th>
                            <th>Disponibilidad</th>
                            <th>Rechazos (Mes)</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {drivers.length === 0 && (
                             <tr>
                                 <td colSpan="10" className="text-center text-muted py-4">
                                     {!searchTerm ? '🔍 Ingrese un término de búsqueda para ver los conductores.' : 'No se encontraron conductores.'}
                                 </td>
                             </tr>
                        )}
                        {drivers.map(d => (
                            <tr key={d.id} className={!d.is_active ? 'table-light opacity-75' : ''}>
                                <td>
                                    <div className="d-flex align-items-center gap-2">
                                        {/* Avatar de Conductor con fallback */}
                                        <div className="position-relative" style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                                            <div 
                                                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold text-uppercase border" 
                                                style={{ width: '32px', height: '32px', fontSize: '11px' }}
                                            >
                                                {d.full_name?.charAt(0)}
                                            </div>
                                            <img 
                                                src={`${apiClient.defaults.baseURL || ''}/transport/drivers/${d.id}/profile-image`} 
                                                alt={d.full_name}
                                                className="rounded-circle position-absolute top-0 start-0 bg-white border"
                                                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="fw-bold">{d.full_name}</span>
                                                {!d.is_active && <Badge bg="secondary">Inactivo</Badge>}
                                                {d.has_pending_documents && (
                                                    <Badge bg="warning" text="dark" className="fw-bold" style={{ fontSize: '10px' }}>
                                                        ⚠️ Pendiente Validar
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="small text-muted" style={{ fontSize: '11px' }}>{d.rut}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    {d.assigned_vehicle_code ? (
                                        <Badge bg="dark" className="border border-primary">
                                            🚌 {d.assigned_vehicle_code}
                                        </Badge>
                                    ) : (
                                        <span className="text-muted small">Sin móvil</span>
                                    )}
                                </td>
                                <td>
                                    {d.license_expiry ? (
                                        (() => {
                                            const todayStr = new Date().toISOString().split('T')[0];
                                            const isExpired = d.license_expiry < todayStr;
                                            const expDate = new Date(d.license_expiry + 'T23:59:59');
                                            const diffDays = Math.ceil((expDate - new Date()) / (1000 * 60 * 60 * 24));
                                            const isSoon = !isExpired && diffDays <= 30;

                                            const [y, m, day] = d.license_expiry.split('-');
                                            const formatted = `${day}/${m}/${y}`;

                                            if (isExpired) {
                                                return (
                                                    <div>
                                                        <Badge bg="danger" title={`Venció hace ${Math.abs(diffDays)} días`}>
                                                            ⛔ Vencida ({formatted})
                                                        </Badge>
                                                        {d.license_type && <div className="text-muted small mt-1" style={{ fontSize: '11px' }}>{d.license_type}</div>}
                                                    </div>
                                                );
                                            }
                                            if (isSoon) {
                                                return (
                                                    <div>
                                                        <Badge bg="warning" text="dark" title={`Vence en ${diffDays} días`}>
                                                            ⚠️ Vence {formatted}
                                                        </Badge>
                                                        {d.license_type && <div className="text-muted small mt-1" style={{ fontSize: '11px' }}>{d.license_type}</div>}
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div>
                                                    <span className="badge bg-light text-dark border">
                                                        ✅ {formatted}
                                                    </span>
                                                    {d.license_type && <div className="text-muted small mt-1" style={{ fontSize: '11px' }}>{d.license_type}</div>}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <span className="text-muted small">No registrada</span>
                                    )}
                                </td>
                                <td>
                                    {d.base_id ? (
                                        <Badge bg="secondary" className="fw-normal">
                                            {bases.find(b => b.id === parseInt(d.base_id))?.name || 'ID: ' + d.base_id}
                                        </Badge>
                                    ) : <span className="text-muted small">- Sin Asignar -</span>}
                                </td>
                                <td>
                                    <div className="d-flex gap-1 flex-wrap">
                                        {d.is_owner && <Badge bg="primary">Propietario</Badge>}
                                        {d.is_driver && <Badge bg="info">Conductor</Badge>}
                                    </div>
                                    {d.employer_id && (
                                        <div className="small text-muted mt-1">
                                            De: {getEmployerName(d.employer_id)}
                                        </div>
                                    )}
                                    {d.provider_id ? (
                                        <div className="mt-1 d-flex flex-column gap-1 align-items-start">
                                            <Badge bg="purple" style={{ backgroundColor: '#a855f7' }}>
                                                Prov: {providers.find(p => p.id === d.provider_id)?.name || d.provider_id}
                                            </Badge>
                                            {d.only_booking && (
                                                <Badge bg="danger" className="text-white">
                                                    Solo Booking
                                                </Badge>
                                            )}
                                        </div>
                                    ) : <div className="small text-muted mt-1">Propio</div>}
                                </td>
                                <td>
                                    {d.contract_id ? (
                                        contracts.find(c => c.id === d.contract_id)?.name || 'ID: ' + d.contract_id
                                    ) : <span className="text-warning small">Sin Contrato</span>}
                                </td>
                                <td>
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge bg={d.has_active_session ? 'success' : 'secondary'}>
                                            {d.has_active_session ? 'Conectado' : 'No Conectado'}
                                        </Badge>
                                        {d.has_active_session ? (
                                            <Button 
                                                size="sm" 
                                                variant="outline-danger"
                                                onClick={() => handleCloseSession(d)}
                                                style={{padding: '0px 6px', fontSize: '0.7em'}}
                                                title="Cerrar Sesión Remota"
                                            >
                                                🔌
                                            </Button>
                                        ) : (
                                            <Button 
                                                size="sm" 
                                                variant="outline-success"
                                                onClick={() => handleOpenSession(d)}
                                                style={{padding: '0px 6px', fontSize: '0.7em'}}
                                                title="Iniciar Sesión Remota"
                                            >
                                                🔑
                                            </Button>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    {(() => {
                                        const status = getDriverStatus(d);
                                        return (
                                            <div className="d-flex flex-column align-items-start">
                                                <Badge bg={status === 'online' ? 'success' : 'danger'}>
                                                    {status === 'online' ? '🟢 Disponible' : '🔴 No Disponible'}
                                                </Badge>
                                                <span className="text-muted" style={{fontSize: '0.75em', marginTop: '2px'}}>
                                                    {formatLastHeartbeat(d)}
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td>
                                    {(d.monthly_rejections_count || 0) > 0 ? (
                                        <Badge bg="danger" className="d-inline-flex align-items-center gap-1">
                                            🚫 {d.monthly_rejections_count}
                                        </Badge>
                                    ) : (
                                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>0</span>
                                    )}
                                </td>
                                <td>
                                    <div className="d-flex gap-1">
                                        <Button size="sm" variant="outline-primary" onClick={() => handleOpenEdit(d)} title="Editar Ficha">✏️</Button>
                                        <Button size="sm" variant="outline-info" onClick={() => handleOpenDocsModal(d)} title="Ver Documentos">📄</Button>
                                        <Button size="sm" variant="outline-warning" onClick={() => { setSelectedDriverForSchedule(d); setShowScheduleModal(true); }} title="Gestionar Horarios y Ausencias">🕒</Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {drivers.length === 0 && (
                            <tr>
                                <td colSpan="10" className="text-center p-4 text-muted">
                                    No se encontraron conductores.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
                
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="small text-muted">Página {page + 1}</div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" disabled={page === 0} onClick={() => setPage(prev => prev - 1)}>Anterior</Button>
                        <Button variant="outline-secondary" size="sm" disabled={!hasMore} onClick={() => setPage(prev => prev + 1)}>Siguiente</Button>
                    </div>
                </div>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton><Modal.Title>{isEditing ? 'Editar Ficha' : 'Nuevo Ingreso'}</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <Row className="mb-3">
                            <Col md={12}><h6 className="text-muted border-bottom pb-1">Datos Personales</h6></Col>
                            <Col md={6}><Form.Label>Nombre Completo</Form.Label><Form.Control required value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} /></Col>
                            <Col md={6}><Form.Label>RUT</Form.Label><Form.Control required value={formData.rut} onChange={e => setFormData({...formData, rut: e.target.value})} /></Col>
                            <Col md={6} className="mt-2"><Form.Label>Teléfono</Form.Label><Form.Control value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} /></Col>
                            <Col md={6} className="mt-2"><Form.Label>Email</Form.Label><Form.Control type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></Col>
                            {isEditing && (
                                <Col md={12} className="mt-2">
                                    <div className="p-2 bg-dark text-white rounded d-flex justify-content-between align-items-center">
                                        <span>Móvil Actualmente Asignado:</span>
                                        <Badge bg="primary" className="fs-6">{drivers.find(d => d.id === editingId)?.assigned_vehicle_code || 'Ninguno'}</Badge>
                                    </div>
                                </Col>
                            )}
                        </Row>

                        <Row className="mb-3 bg-light p-2 rounded mx-0 border">
                            <Col md={12}><h6 className="text-danger border-bottom pb-1">🪪 Licencia de Conducir (MTT)</h6></Col>
                            <Col md={6}>
                                <Form.Label>Tipo / Clase de Licencia</Form.Label>
                                <Form.Control 
                                    value={formData.license_type} 
                                    placeholder="Ej: A2, A3, Profesional" 
                                    onChange={e => setFormData({...formData, license_type: e.target.value})} 
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-bold">Fecha de Vencimiento de Licencia</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    value={formData.license_expiry} 
                                    onChange={e => setFormData({...formData, license_expiry: e.target.value})} 
                                />
                                <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                    Al vencer, el despachador alertará y no permitirá asignarle viajes en fechas posteriores.
                                </Form.Text>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}><h6 className="text-muted border-bottom pb-1">Operación y Finanzas</h6></Col>
                            <Col md={6}>
                                <Form.Label className="fw-bold text-success">Base Principal (Pago)</Form.Label>
                                <Form.Select required value={formData.base_id} onChange={e => setFormData({...formData, base_id: e.target.value})}>
                                    <option value="">- Seleccionar Moneda/Base -</option>
                                    {bases.map(b => <option key={b.id} value={b.id}>{b.name} ({b.currency_code})</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label>Contrato</Form.Label>
                                <Form.Select required value={formData.contract_id} onChange={e => setFormData({...formData, contract_id: e.target.value})}>
                                    <option value="">- Seleccione -</option>
                                    {contracts.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={12} className="mt-2">
                                <Form.Label>Grupo de Liquidación</Form.Label>
                                <Form.Select value={formData.settlement_group_id} onChange={e => setFormData({...formData, settlement_group_id: e.target.value})}>
                                    <option value="">Ninguno (Sin Grupo)</option>
                                    {settlementGroups.map(sg => <option key={sg.id} value={sg.id}>{sg.name}</option>)}
                                </Form.Select>
                            </Col>
                        </Row>

                        <Row className="mb-3 bg-light p-2 rounded mx-0 border">
                            <Col md={12}><h6 className="text-primary border-bottom pb-1">Datos Bancarios (Pago)</h6></Col>
                            <Col md={4}>
                                <Form.Label>Banco</Form.Label>
                                <Form.Select value={formData.bank_id} onChange={e => setFormData({...formData, bank_id: e.target.value})}>
                                    <option value="">- Seleccione Banco -</option>
                                    {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </Form.Select>
                                {formData.bank_id && (
                                    <div className="small text-muted mt-1">
                                        Código Banco: <strong>{banks.find(b => String(b.id) === String(formData.bank_id))?.code || '---'}</strong>
                                    </div>
                                )}
                            </Col>
                            <Col md={4}>
                                <Form.Label>Tipo Cuenta</Form.Label>
                                <Form.Select value={formData.account_type} onChange={e => setFormData({...formData, account_type: e.target.value})}>
                                    <option value="">- Seleccione -</option>
                                    <option value="VISTA">Cuenta Vista / RUT</option>
                                    <option value="CORRIENTE">Cuenta Corriente</option>
                                    <option value="AHORRO">Cuenta de Ahorro</option>
                                </Form.Select>
                            </Col>
                            <Col md={4}>
                                <Form.Label>RUT Titular Cuenta</Form.Label>
                                <Form.Control value={formData.account_rut} placeholder="Ej: 12.345.678-9" onChange={e => setFormData({...formData, account_rut: e.target.value})} />
                            </Col>
                            <Col md={6} className="mt-2">
                                <Form.Label>Nº Cuenta</Form.Label>
                                <Form.Control value={formData.account_number} onChange={e => setFormData({...formData, account_number: e.target.value})} />
                            </Col>
                            <Col md={6} className="mt-2">
                                <Form.Label>Email Aviso de Pago</Form.Label>
                                <Form.Control type="email" value={formData.email_notification} onChange={e => setFormData({...formData, email_notification: e.target.value})} />
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Label className="fw-bold text-primary">Zonas Permitidas (Operación)</Form.Label>
                                <MultiSearchSelect 
                                    options={bases}
                                    value={formData.permitted_base_ids}
                                    onChange={(val) => setFormData({ ...formData, permitted_base_ids: val })}
                                    placeholder="Buscar bases/zonas permitidas..."
                                    labelField="name"
                                />
                                <small className="text-muted italic">Bases o sucursales donde el conductor puede operar.</small>
                            </Col>
                            <Col md={12} className="mt-3">
                                <Form.Label className="fw-bold text-danger">Pool de Móviles Autorizados</Form.Label>
                                <MultiSearchSelect 
                                    options={vehicles}
                                    value={formData.permitted_vehicle_ids}
                                    onChange={(val) => setFormData({ ...formData, permitted_vehicle_ids: val })}
                                    placeholder="Buscar por Patente o Código..."
                                    labelField="internal_code"
                                />
                                <Form.Text className="text-muted italic">Vehículos que este conductor tiene permitido utilizar.</Form.Text>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md={12}><h6 className="text-muted border-bottom pb-1">Roles</h6></Col>
                            <Col md={12} className="mb-2">
                                <Form.Check inline label="Es Propietario" checked={formData.is_owner} onChange={e => setFormData({...formData, is_owner: e.target.checked})} />
                                <Form.Check inline label="Es Conductor" checked={formData.is_driver} onChange={e => setFormData({...formData, is_driver: e.target.checked})} />
                                <Form.Check inline label="Aprobado (Onboarding)" checked={formData.is_approved} onChange={e => setFormData({...formData, is_approved: e.target.checked})} />
                                <Form.Check inline label="Mostrar Precios en Portal" checked={formData.show_pricing} onChange={e => setFormData({...formData, show_pricing: e.target.checked})} />
                                <Form.Check inline label="Conductor Activo" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Empleador (Dueño)</Form.Label>
                                <Form.Select value={formData.employer_id} onChange={e => setFormData({...formData, employer_id: e.target.value})} disabled={formData.is_owner && !formData.is_driver}>
                                    <option value="">- Independiente / Mismo -</option>
                                    {owners.map(o => <option key={o.id} value={o.id}>{o.full_name}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label>Proveedor Asociado</Form.Label>
                                <Form.Select value={formData.provider_id} onChange={e => {
                                    const newProvId = e.target.value;
                                    const selProv = providers.find(p => String(p.id) === String(newProvId));
                                    let nextContractId = formData.contract_id;
                                    let nextEmployerId = formData.employer_id;
                                    let nextBaseId = formData.base_id;
                                    let nextGroupId = formData.settlement_group_id;
                                    let nextPermittedBases = formData.permitted_base_ids;

                                    if (selProv) {
                                        const ownerObj = selProv.owner_id ? owners.find(o => Number(o.id) === Number(selProv.owner_id)) : null;
                                        if (selProv.hybrid_settlement) {
                                            const ownerContract = ownerObj?.contract_id ? contracts.find(c => Number(c.id) === Number(ownerObj.contract_id)) : null;
                                            const varContract = (ownerContract && ownerContract.type === 'VARIABLE')
                                                ? ownerContract
                                                : contracts.find(c => c.type === 'VARIABLE');
                                            if (varContract) nextContractId = String(varContract.id);
                                        } else if (selProv.contract_id) {
                                            nextContractId = String(selProv.contract_id);
                                        } else if (ownerObj?.contract_id) {
                                            nextContractId = String(ownerObj.contract_id);
                                        }
                                        if (selProv.owner_id && !formData.is_owner) {
                                            nextEmployerId = String(selProv.owner_id);
                                        }
                                        if (selProv.base_id && !nextBaseId) {
                                            nextBaseId = String(selProv.base_id);
                                        }
                                        if (selProv.settlement_group_id && !nextGroupId) {
                                            nextGroupId = String(selProv.settlement_group_id);
                                        }
                                        if (Array.isArray(selProv.bases) && selProv.bases.length > 0 && (!nextPermittedBases || nextPermittedBases.length === 0)) {
                                            nextPermittedBases = selProv.bases.map(b => b.id);
                                        }
                                    }

                                    setFormData({
                                        ...formData, 
                                        provider_id: newProvId,
                                        contract_id: nextContractId,
                                        employer_id: nextEmployerId,
                                        base_id: nextBaseId,
                                        settlement_group_id: nextGroupId,
                                        permitted_base_ids: nextPermittedBases,
                                        only_booking: newProvId ? formData.only_booking : false
                                    });
                                }}>
                                    <option value="">- Ninguno (Chofer Propio) -</option>
                                    {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </Form.Select>
                            </Col>
                        </Row>
                        {formData.provider_id && (
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Group className="p-2.5 border rounded bg-light">
                                        <Form.Label className="small fw-bold mb-1 d-block text-dark">
                                            Restricción Operativa de Convenios
                                        </Form.Label>
                                        <div className="d-flex flex-column gap-1">
                                            <Form.Check
                                                type="radio"
                                                id="operacion-todos"
                                                name="only_booking_radio"
                                                label="Habilitado para todos los convenios (Booking.com + Convenios Corporativos)"
                                                checked={!formData.only_booking}
                                                onChange={() => setFormData({...formData, only_booking: false})}
                                                className="small"
                                            />
                                            <Form.Check
                                                type="radio"
                                                id="operacion-solo-booking"
                                                name="only_booking_radio"
                                                label="Restringido solo a Booking.com (Bloqueado para viajes corporativos)"
                                                checked={!!formData.only_booking}
                                                onChange={() => setFormData({...formData, only_booking: true})}
                                                className="small text-danger fw-bold"
                                            />
                                        </div>
                                        <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                            Si está restringido a Booking, las locutoras no podrán asignarle viajes de convenios corporativos (Godrej, etc.).
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit">{isEditing ? 'Guardar Cambios' : 'Registrar Persona'}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showContractModal} onHide={() => setShowContractModal(false)}>
                <Modal.Header closeButton><Modal.Title>Nuevo Contrato</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Nombre Contrato</Form.Label>
                        <Form.Control placeholder="Ej. Variable Estandar" value={newContract.name} onChange={e => setNewContract({...newContract, name: e.target.value})} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo</Form.Label>
                        <Form.Select value={newContract.type} onChange={e => setNewContract({...newContract, type: e.target.value})}>
                            <option value="FIJO">Pago Fijo (100% Base)</option>
                            <option value="VARIABLE">Variable (Porcentaje)</option>
                        </Form.Select>
                    </Form.Group>
                    {newContract.type === 'VARIABLE' && (
                        <div className="p-3 bg-light rounded">
                            <h6>Reglas de Porcentaje</h6>
                            <Row>
                                <Col md={6}>
                                    <Form.Label>% Bajo Umbral</Form.Label>
                                    <InputGroup><Form.Control type="number" value={newContract.percentage_1} onChange={e => setNewContract({...newContract, percentage_1: parseFloat(e.target.value)})} /><InputGroup.Text>%</InputGroup.Text></InputGroup>
                                </Col>
                                <Col md={6}>
                                    <Form.Label>% Sobre Umbral</Form.Label>
                                    <InputGroup><Form.Control type="number" value={newContract.percentage_2} onChange={e => setNewContract({...newContract, percentage_2: parseFloat(e.target.value)})} /><InputGroup.Text>%</InputGroup.Text></InputGroup>
                                </Col>
                                <Col md={12} className="mt-2">
                                    <Form.Label>Umbral de Corte ($)</Form.Label>
                                    <Form.Control type="number" value={newContract.threshold} onChange={e => setNewContract({...newContract, threshold: parseFloat(e.target.value)})} />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer><Button variant="primary" onClick={handleContractSubmit}>Crear Contrato</Button></Modal.Footer>
            </Modal>

            {/* Modal de Documentos de Conductores */}
            <Modal show={showDocsModal} onHide={() => setShowDocsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center gap-2">
                        {selectedDriverForDocs && (
                            <div className="position-relative" style={{ width: '36px', height: '36px' }}>
                                <div 
                                    className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold text-uppercase border" 
                                    style={{ width: '36px', height: '36px', fontSize: '12px' }}
                                >
                                    {selectedDriverForDocs.full_name?.charAt(0)}
                                </div>
                                <img 
                                    src={`${apiClient.defaults.baseURL || ''}/transport/drivers/${selectedDriverForDocs.id}/profile-image`} 
                                    alt={selectedDriverForDocs.full_name}
                                    className="rounded-circle position-absolute top-0 start-0 bg-white border"
                                    style={{ width: '36px', height: '36px', objectFit: 'cover' }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}
                        <span>📄 Documentos de Conductor: <span className="text-primary">{selectedDriverForDocs?.full_name}</span></span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingDocs ? (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Cargando documentos de AWS S3...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table bordered hover align="middle" className="mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Tipo de Documento</th>
                                        <th>Estado</th>
                                        <th>Vencimiento</th>
                                        <th className="text-center">Archivo</th>
                                        <th className="text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {REQUIRED_DOC_TYPES.map(type => {
                                        const doc = driverDocs.find(d => d.document_type === type.key) || driverDocs.find(d =>
                                            (type.key === 'licencia_frontal' && d.document_type === 'licencia') ||
                                            (type.key === 'rut_frontal' && d.document_type === 'rut') ||
                                            (type.key === 'decreto_mtt' && d.document_type === 'certificado_mtt')
                                        );
                                        return (
                                            <tr key={type.key}>
                                                <td>
                                                    <div className="fw-bold">{type.label}</div>
                                                    <div className="small text-muted">
                                                        <Badge bg="light" text="dark" className="border me-1">{type.group}</Badge>
                                                        {type.is_critical && <Badge bg="danger" className="me-1">Clave (Bloqueante)</Badge>}
                                                    </div>
                                                </td>
                                                <td>
                                                    {doc ? (
                                                        <>
                                                            {doc.status === 'approved' && (
                                                                (() => {
                                                                    if (!doc.expiration_date) return <Badge bg="success">🟢 Aprobado</Badge>;
                                                                    const cleanStr = typeof doc.expiration_date === 'string' ? doc.expiration_date.split('T')[0] : '';
                                                                    const exp = new Date(cleanStr + 'T12:00:00');
                                                                    const parts = cleanStr.split('-');
                                                                    const dateFmt = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : exp.toLocaleDateString('es-CL');
                                                                    const today = new Date();
                                                                    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
                                                                    if (diffDays < 0) return <Badge bg="danger">🔴 VENCIDO ({dateFmt})</Badge>;
                                                                    if (diffDays <= 30) return <Badge bg="warning" text="dark">🟡 POR VENCER ({dateFmt})</Badge>;
                                                                    return <Badge bg="success">🟢 VIGENTE ({dateFmt})</Badge>;
                                                                })()
                                                            )}
                                                            {doc.status === 'pending' && <Badge bg="info" className="text-white">🟡 Pendiente de Revisión</Badge>}
                                                            {doc.status === 'rejected' && (
                                                                <div className="d-flex flex-column align-items-start">
                                                                    <Badge bg="danger">🔴 Rechazado</Badge>
                                                                    <small className="text-danger mt-1">Motivo: {doc.rejection_reason}</small>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Badge bg="secondary">⚪ No Registrado</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    {doc?.expiration_date ? (
                                                        <span className="small font-monospace">
                                                            {(() => {
                                                                const cleanStr = typeof doc.expiration_date === 'string' ? doc.expiration_date.split('T')[0] : '';
                                                                const parts = cleanStr.split('-');
                                                                return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : new Date(doc.expiration_date).toLocaleDateString('es-CL');
                                                            })()}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted small">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {doc?.url ? (
                                                        <a href={doc.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary fw-bold">
                                                            Visualizar 🔗
                                                        </a>
                                                    ) : (
                                                        <span className="text-muted small">Sin archivo</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex flex-column gap-2 align-items-center">
                                                        {doc && (
                                                            <Button size="sm" variant="outline-dark" onClick={() => handleReviewClick(doc)}>
                                                                ⚖️ Evaluar
                                                            </Button>
                                                        )}
                                                        
                                                        {uploadingDocType === type.key ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <Form.Group controlId={`file-${type.key}`}>
                                                                <Form.Control 
                                                                    type="file" 
                                                                    size="sm" 
                                                                    onChange={(e) => handleUploadDoc(type.key, e.target.files[0])}
                                                                    style={{ maxWidth: '180px', fontSize: '0.8em' }}
                                                                    title="Subir archivo"
                                                                />
                                                            </Form.Group>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDocsModal(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Mini de Aprobación/Rechazo de Documento */}
            <Modal show={showReviewConfirmModal} onHide={() => setShowReviewConfirmModal(false)} centered>
                <Form onSubmit={handleReviewSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>⚖️ Evaluar Documento</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Decisión</Form.Label>
                            <Form.Select value={reviewStatus} onChange={e => setReviewStatus(e.target.value)}>
                                <option value="approved">Aprobar Documento</option>
                                <option value="rejected">Rechazar Documento</option>
                            </Form.Select>
                        </Form.Group>

                        {reviewStatus === 'approved' ? (
                            <Form.Group className="mb-3">
                                <Form.Label>Fecha de Vencimiento (Opcional)</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    value={docExpirationDate} 
                                    onChange={e => setDocExpirationDate(e.target.value)} 
                                />
                                <Form.Text className="text-muted">Si el documento expira, el sistema enviará alertas automáticamente.</Form.Text>
                            </Form.Group>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label>Motivo de Rechazo (Obligatorio)</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3} 
                                    required 
                                    value={rejectionReason} 
                                    onChange={e => setRejectionReason(e.target.value)} 
                                    placeholder="Detalla la razón por la cual no se acepta el documento..."
                                />
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowReviewConfirmModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit">Guardar Decisión</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de Disponibilidad, Horario y Ausencias (Admin) */}
            <DriverAvailabilityModal 
                show={showScheduleModal} 
                onHide={() => {
                    setShowScheduleModal(false);
                    setSelectedDriverForSchedule(null);
                }} 
                driverId={selectedDriverForSchedule?.id} 
                driverName={selectedDriverForSchedule?.full_name} 
                onSaved={loadData} 
            />
        </div>
    );
};

export default DriversView;
